import { useEffect } from 'react';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import { FormPageLayout } from '@/components/layouts';
import { InvoiceFormPage } from '@/components/organisms/invoices/InvoiceFormPage';
import { useInvoice } from '@/hooks/invoices/useInvoice';
import { useHasPermission, useIsFieldHidden } from '@/hooks/usePermissions';

export const Route = createFileRoute('/_dashboard/dashboard/invoices/$id/edit')({
  component: EditInvoiceRoute,
});

function EditInvoiceRoute() {
  const { id } = Route.useParams();
  const router = useRouter();
  const pricingHidden = useIsFieldHidden('PRICING');
  const paymentInfoHidden = useIsFieldHidden('PAYMENT_INFO');
  const financialsHidden = useIsFieldHidden('CUSTOMER_FINANCIALS');
  const canEdit = useHasPermission('invoices:edit');
  const { data: invoice, isLoading } = useInvoice(id);

  const editable = Boolean(invoice && ['draft', 'open', 'sent', 'viewed', 'overdue'].includes(invoice.status));
  useEffect(() => {
    if (invoice && !editable) {
      void router.navigate({
        to: '/dashboard/invoices/$id',
        params: { id },
        search: (prev) => prev as never,
      });
    }
  }, [invoice, id, router, editable]);

  if (pricingHidden || paymentInfoHidden || financialsHidden || !canEdit) {
    return (
      <FormPageLayout
        title="Invoice editing unavailable"
        onBack={() =>
          void router.navigate({
            to: '/dashboard/invoices/$id',
            params: { id },
            search: (prev) => prev as never,
          })
        }
      >
        <div className="mx-auto max-w-xl px-4 py-6">
          <div className="rounded-lg border bg-muted/40 p-4">
            <p className="font-medium">Invoice editing is unavailable for your role.</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Return to the invoice to review its operational details.
            </p>
          </div>
        </div>
      </FormPageLayout>
    );
  }

  if (isLoading) {
    return (
      <FormPageLayout
        title="Edit invoice"
        noscroll
        onBack={() =>
          void router.navigate({
            to: '/dashboard/invoices/$id',
            params: { id },
            search: (prev) => prev as never,
          })
        }
      >
        <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
          <Loader2 className="mr-2 size-4 animate-spin" />
          Loading invoice…
        </div>
      </FormPageLayout>
    );
  }

  if (!invoice || !editable) return null;

  return <InvoiceFormPage invoiceId={id} mode="edit" />;
}
